import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Heart, ArrowLeft, Download, Trash2, Users, Calendar, 
  MapPin, Clock, Mail, Phone, GraduationCap, Pencil, UserPlus, Upload, FileSpreadsheet 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSignups = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [signups, setSignups] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteWaitlistDialog, setShowDeleteWaitlistDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedSignup, setSelectedSignup] = useState(null);
  const [selectedWaitlistEntry, setSelectedWaitlistEntry] = useState(null);
  const fileInputRef = useRef(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    age_grade: '',
    volunteer_type: 'student'
  });
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [opportunityId]);

  const fetchData = async () => {
    try {
      const [oppRes, signupsRes, waitlistRes] = await Promise.all([
        axios.get(`${API}/opportunities/${opportunityId}`),
        axios.get(`${API}/signups/${opportunityId}`, { headers }),
        axios.get(`${API}/waitlist/${opportunityId}`, { headers })
      ]);
      setOpportunity(oppRes.data);
      setSignups(signupsRes.data);
      setWaitlist(waitlistRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/signups/${opportunityId}/export`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${opportunity?.title?.replace(/\s+/g, '_') || 'signups'}_signups.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleDeleteSignup = async () => {
    try {
      await axios.delete(`${API}/signups/${selectedSignup.id}`, { headers });
      toast.success('Sign-up removed');
      setShowDeleteDialog(false);
      setSelectedSignup(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove sign-up');
    }
  };

  const handleDeleteWaitlist = async () => {
    try {
      await axios.delete(`${API}/waitlist/${selectedWaitlistEntry.id}`, { headers });
      toast.success('Removed from waitlist');
      setShowDeleteWaitlistDialog(false);
      setSelectedWaitlistEntry(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove from waitlist');
    }
  };

  const openEditModal = (signup) => {
    setSelectedSignup(signup);
    setEditForm({
      first_name: signup.first_name,
      last_name: signup.last_name,
      email: signup.email,
      phone: signup.phone,
      age_grade: signup.age_grade,
      volunteer_type: signup.volunteer_type
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API}/signups/${selectedSignup.id}`, editForm, { headers });
      toast.success('Volunteer updated!');
      setShowEditModal(false);
      setSelectedSignup(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/signups/${opportunityId}/import`,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setImportResult(response.data);
      if (response.data.success_count > 0) {
        toast.success(`Imported ${response.data.success_count} volunteer(s)!`);
        fetchData();
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} row(s) had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
      setImportResult({ success_count: 0, error_count: 1, errors: [error.response?.data?.detail || 'Import failed'], imported: [] });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = `First Name,Last Name,Email,Phone,Age/Grade,Type
John,Doe,john.doe@example.com,555-123-4567,8th Grade,student
Jane,Smith,jane.smith@example.com,555-987-6543,Adult,parent`;
    
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'volunteer_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatSignupDate = (isoStr) => {
    const date = new Date(isoStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const studentSignups = signups.filter(s => s.volunteer_type === 'student');
  const parentSignups = signups.filter(s => s.volunteer_type === 'parent');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const renderSignupTable = (signupList, type) => (
    <Card className="rounded-3xl border-border/50 overflow-hidden">
      <CardHeader className={type === 'student' ? 'bg-primary/5' : 'bg-secondary/10'}>
        <CardTitle className="flex items-center gap-2 font-[Nunito]">
          {type === 'student' ? (
            <><GraduationCap className="w-5 h-5 text-primary" /> Student Volunteers ({signupList.length})</>
          ) : (
            <><Users className="w-5 h-5 text-secondary-foreground" /> Parent Volunteers ({signupList.length})</>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table data-testid={`${type}-signups-table`}>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>{type === 'student' ? 'Grade' : 'Info'}</TableHead>
                <TableHead>Signed Up</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signupList.map((signup) => (
                <TableRow key={signup.id} data-testid={`signup-row-${signup.id}`}>
                  <TableCell className="font-medium">
                    {signup.first_name} {signup.last_name}
                  </TableCell>
                  <TableCell>
                    <a href={`mailto:${signup.email}`} className="flex items-center gap-1 text-primary hover:underline">
                      <Mail className="w-3 h-3" />
                      {signup.email}
                    </a>
                  </TableCell>
                  <TableCell>
                    <a href={`tel:${signup.phone}`} className="flex items-center gap-1 hover:text-primary">
                      <Phone className="w-3 h-3" />
                      {signup.phone}
                    </a>
                  </TableCell>
                  <TableCell>{signup.age_grade}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatSignupDate(signup.signed_up_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-primary"
                        onClick={() => openEditModal(signup)}
                        data-testid={`edit-signup-${signup.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => { setSelectedSignup(signup); setShowDeleteDialog(true); }}
                        data-testid={`delete-signup-${signup.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Decorative blobs */}
      <div className="bg-blob w-96 h-96 bg-primary -top-48 -left-48 fixed" />
      <div className="bg-blob w-80 h-80 bg-secondary top-1/2 -right-40 fixed" />

      {/* Header */}
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-extrabold text-foreground font-[Nunito]">GYVC | VolunteerHub</h1>
            </Link>
            <Badge variant="secondary" className="ml-4">Admin</Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <Link 
          to="/admin/dashboard"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Opportunity Details */}
        <Card className="rounded-3xl border-border/50 mb-8">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-2xl md:text-3xl font-bold font-[Nunito]" data-testid="opportunity-title">
                  {opportunity?.title}
                </CardTitle>
                <p className="text-muted-foreground mt-2">{opportunity?.description}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button 
                  variant="outline"
                  onClick={() => setShowImportModal(true)}
                  className="rounded-full font-bold gap-2"
                  data-testid="import-csv-btn"
                >
                  <Upload className="w-4 h-4" />
                  Import CSV
                </Button>
                <Button 
                  onClick={handleExport}
                  className="rounded-full font-bold gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-foreground">
                <Calendar className="w-4 h-4 text-primary" />
                <span>{opportunity && formatDate(opportunity.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <Clock className="w-4 h-4 text-primary" />
                <span>{opportunity?.time}</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <MapPin className="w-4 h-4 text-primary" />
                <span>{opportunity?.location}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-primary/10 rounded-2xl p-4">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <GraduationCap className="w-5 h-5" />
                  <span className="font-semibold">Student Volunteers</span>
                </div>
                <p className="text-2xl font-bold font-[Nunito]" data-testid="student-count">
                  {opportunity?.student_signups} / {opportunity?.student_slots}
                </p>
              </div>
              <div className="bg-secondary/30 rounded-2xl p-4">
                <div className="flex items-center gap-2 text-secondary-foreground mb-1">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">Parent Volunteers</span>
                </div>
                <p className="text-2xl font-bold font-[Nunito]" data-testid="parent-count">
                  {opportunity?.parent_signups} / {opportunity?.parent_slots}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signups Tables */}
        {signups.length === 0 ? (
          <Card className="rounded-3xl border-border/50 py-16 text-center">
            <CardContent>
              <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-bold font-[Nunito] mb-2">No sign-ups yet</h3>
              <p className="text-muted-foreground">Share this opportunity to get volunteers!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {studentSignups.length > 0 && renderSignupTable(studentSignups, 'student')}
            {parentSignups.length > 0 && renderSignupTable(parentSignups, 'parent')}
          </div>
        )}

        {/* Waitlist Section */}
        {waitlist.length > 0 && (
          <div className="mt-8">
            <Card className="rounded-3xl border-border/50 border-dashed">
              <CardHeader className="bg-muted/30">
                <CardTitle className="flex items-center gap-2 font-[Nunito]">
                  <UserPlus className="w-5 h-5 text-muted-foreground" />
                  Waitlist ({waitlist.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table data-testid="waitlist-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {waitlist.map((entry) => (
                        <TableRow key={entry.id} data-testid={`waitlist-row-${entry.id}`}>
                          <TableCell className="font-medium text-muted-foreground">
                            {entry.position}
                          </TableCell>
                          <TableCell className="font-medium">
                            {entry.first_name} {entry.last_name}
                          </TableCell>
                          <TableCell>
                            <a href={`mailto:${entry.email}`} className="flex items-center gap-1 text-primary hover:underline">
                              <Mail className="w-3 h-3" />
                              {entry.email}
                            </a>
                          </TableCell>
                          <TableCell>
                            <a href={`tel:${entry.phone}`} className="flex items-center gap-1 hover:text-primary">
                              <Phone className="w-3 h-3" />
                              {entry.phone}
                            </a>
                          </TableCell>
                          <TableCell>
                            <Badge variant={entry.volunteer_type === 'student' ? 'default' : 'secondary'} className="capitalize">
                              {entry.volunteer_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatSignupDate(entry.joined_at)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => { setSelectedWaitlistEntry(entry); setShowDeleteWaitlistDialog(true); }}
                              data-testid={`delete-waitlist-${entry.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="edit-signup-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-[Nunito]">
              Edit Volunteer
            </DialogTitle>
            <DialogDescription>
              Update volunteer information
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_first_name">First Name</Label>
                <Input
                  id="edit_first_name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="edit-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_last_name">Last Name</Label>
                <Input
                  id="edit_last_name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="edit-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                required
                className="rounded-xl h-11"
                data-testid="edit-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_phone">Phone</Label>
              <Input
                id="edit_phone"
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                required
                className="rounded-xl h-11"
                data-testid="edit-phone"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_age_grade">Age/Grade</Label>
              <Input
                id="edit_age_grade"
                value={editForm.age_grade}
                onChange={(e) => setEditForm(f => ({ ...f, age_grade: e.target.value }))}
                required
                className="rounded-xl h-11"
                data-testid="edit-age-grade"
              />
            </div>
            
            <div className="space-y-3">
              <Label>Volunteer Type</Label>
              <RadioGroup
                value={editForm.volunteer_type}
                onValueChange={(value) => setEditForm(f => ({ ...f, volunteer_type: value }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="student" id="edit_student" data-testid="edit-type-student" />
                  <Label htmlFor="edit_student" className="cursor-pointer">Student</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="parent" id="edit_parent" data-testid="edit-type-parent" />
                  <Label htmlFor="edit_parent" className="cursor-pointer">Parent</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditModal(false)}
                className="flex-1 rounded-full"
                data-testid="edit-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 rounded-full font-bold"
                data-testid="edit-submit-btn"
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-signup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-[Nunito]">Remove Volunteer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {selectedSignup?.first_name} {selectedSignup?.last_name} from this opportunity. 
              They will need to sign up again if they wish to volunteer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-signup-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSignup}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-signup-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Waitlist Confirmation */}
      <AlertDialog open={showDeleteWaitlistDialog} onOpenChange={setShowDeleteWaitlistDialog}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-waitlist-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-[Nunito]">Remove from Waitlist?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {selectedWaitlistEntry?.first_name} {selectedWaitlistEntry?.last_name} from the waitlist.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-waitlist-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteWaitlist}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-waitlist-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import CSV Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => { setShowImportModal(open); if (!open) setImportResult(null); }}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="import-csv-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-[Nunito] flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-primary" />
              Import Volunteers from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import volunteers. No confirmation emails will be sent.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-accent/50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium">Required CSV columns:</p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• <strong>First Name</strong></li>
                <li>• <strong>Last Name</strong></li>
                <li>• <strong>Email</strong></li>
                <li>• <strong>Type</strong> (student or parent)</li>
                <li>• Phone (optional)</li>
                <li>• Age/Grade (optional)</li>
              </ul>
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto text-primary"
                onClick={downloadSampleCSV}
                data-testid="download-template-btn"
              >
                Download sample template
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-file">Select CSV File</Label>
              <Input
                ref={fileInputRef}
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                disabled={importing}
                className="rounded-xl"
                data-testid="csv-file-input"
              />
            </div>

            {importing && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                <span className="ml-3 text-muted-foreground">Importing...</span>
              </div>
            )}

            {importResult && (
              <div className="space-y-3">
                <div className="flex gap-4">
                  <div className="flex-1 bg-green-100 dark:bg-green-900/30 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-green-700 dark:text-green-400">{importResult.success_count}</p>
                    <p className="text-xs text-green-600 dark:text-green-500">Imported</p>
                  </div>
                  <div className="flex-1 bg-red-100 dark:bg-red-900/30 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-red-700 dark:text-red-400">{importResult.error_count}</p>
                    <p className="text-xs text-red-600 dark:text-red-500">Errors</p>
                  </div>
                </div>

                {importResult.imported.length > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">Imported:</p>
                    <ul className="text-xs text-green-600 dark:text-green-500 space-y-0.5">
                      {importResult.imported.map((name, i) => (
                        <li key={i}>✓ {name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {importResult.errors.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">Errors:</p>
                    <ul className="text-xs text-red-600 dark:text-red-500 space-y-0.5">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>✗ {err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { setShowImportModal(false); setImportResult(null); }}
                className="flex-1 rounded-full"
                data-testid="import-close-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSignups;
